import type { ContentCategoryPublic } from '../types';
import { getImageUrl } from './imageUtils';

export type ContentCategoryTreeNode = ContentCategoryPublic & {
  children?: ContentCategoryTreeNode[];
};

export function isUsableCourseCategory(c: ContentCategoryPublic): boolean {
  return c.isActive !== false && Boolean(c.slug?.trim()) && !c.browseLandingIntro;
}

/**
 * مجموع دسته‌های فعال (ریشه + فرزند) برای بج روی کارت «دوره‌ها»
 */
export function countUsableCourseCategories(nodes: ContentCategoryTreeNode[]): number {
  let total = 0;
  function walk(arr: ContentCategoryTreeNode[]): void {
    for (const c of arr) {
      if (isUsableCourseCategory(c)) total += 1;
      walk(c.children ?? []);
    }
  }
  walk(nodes);
  return total;
}

export type CourseDiscoveryPreviewItem = {
  id: string;
  label: string;
  href: string;
  badge?: string;
  thumbnailUrl?: string | null;
  /** توضیح کوتاه زیر عنوان */
  description?: string | null;
  accentColor?: string | null;
};

export type CourseDiscoveryPreviewColumn = {
  id: string;
  title: string;
  subtitle: string;
  items: CourseDiscoveryPreviewItem[];
};

/**
 * درخت GET /categories/tree?type=COURSE → ستون‌های بخش «دسته‌بندی دوره‌ها» در صفحه اصلی
 */
export function buildCourseDiscoveryPreviewGroups(tree: ContentCategoryTreeNode[]): CourseDiscoveryPreviewColumn[] {
  const roots = [...tree]
    .filter(isUsableCourseCategory)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name, 'fa'));

  const groups: CourseDiscoveryPreviewColumn[] = roots.map((root) => {
    const rawChildren = [...(root.children ?? [])].filter(isUsableCourseCategory);
    rawChildren.sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name, 'fa'),
    );

    const itemsFromChildren = rawChildren.map((ch) => {
      const n = ch._count?.courses ?? 0;
      const thumb = getImageUrl(ch.image);
      const desc = ch.shortDescription?.trim() || ch.description?.trim()?.slice(0, 120)?.replace(/\s+/g, ' ') || null;
      return {
        id: ch.id,
        label: ch.name,
        href: `/courses?cat=${encodeURIComponent(ch.slug)}`,
        badge: n > 0 ? `${n.toLocaleString('fa-IR')} دوره` : undefined,
        thumbnailUrl: thumb,
        description: desc,
        accentColor: ch.color ?? null,
      };
    });

    if (itemsFromChildren.length > 0) {
      return {
        id: root.id,
        title: root.name,
        subtitle: `${itemsFromChildren.length.toLocaleString('fa-IR')} زیر‌دسته`,
        items: itemsFromChildren,
      };
    }

    if (root.slug) {
      const n = root._count?.courses ?? 0;
      const thumb = getImageUrl(root.image);
      const desc =
        root.shortDescription?.trim() ||
        root.description?.trim()?.slice(0, 120)?.replace(/\s+/g, ' ') ||
        null;
      return {
        id: root.id,
        title: root.name,
        subtitle: n > 0 ? `${n.toLocaleString('fa-IR')} دوره` : 'مشاهده در کتابخانه',
        items: [
          {
            id: root.id,
            label: root.name,
            href: `/courses?cat=${encodeURIComponent(root.slug)}`,
            badge: n > 0 ? `${n.toLocaleString('fa-IR')} دوره` : undefined,
            thumbnailUrl: thumb,
            description: desc,
            accentColor: root.color ?? null,
          },
        ],
      };
    }

    return { id: root.id, title: root.name, subtitle: '', items: [] };
  });

  const nonempty = groups.filter((g) => g.items.length > 0);
  if (nonempty.length > 0) return nonempty;

  return [
    {
      id: 'course-discovery-fallback',
      title: 'کتابخانه دوره‌ها',
      subtitle: 'همهٔ دوره‌های منتشرشده',
      items: [{ id: 'all-courses', label: 'مشاهده کتابخانه', href: '/courses' }],
    },
  ];
}
